// The motion display: which crank angle the scene draws, from the angle being
// heard.
//
// A 60 Hz display cannot show a crank turning 15 to 80 times a second: the
// frames alias, and the parts seem to jump or run backwards. So the scene draws
// a strobe view by default (decision D14, ADR 0002), with true speed one click
// away:
//
// - "true-speed" draws the heard angle itself.
// - "strobe" draws an angle that moves forward by at most one revolution per
//   second and by at most 30° in any one frame. While the engine turns no
//   faster than that, the drawn angle is the heard angle. Faster, it falls
//   behind and slips through the cycle slowly, so every pose it draws is one
//   the engine passed through within the last cycle, as under a stroboscope
//   whose flash is timed a little later on every turn. When the engine slows
//   down again, the drawn angle catches up with the heard one, moving forward
//   only, and locks to it.
//
// Angles are crank radians in the 720° cycle, [0, 4π).

import { CYCLE, wrapCycle } from "./pose";

export type DisplayMode = "strobe" | "true-speed";

/** One revolution per second. */
export const STROBE_MAX_RATE = 2 * Math.PI;
/** The largest step of the strobe angle in one frame: 30°. */
export const STROBE_MAX_STEP = Math.PI / 6;
/** A heard angle this far behind the drawn one counts as a step back, not as most of a cycle ahead. */
const BACKWARD_WINDOW = Math.PI / 2;

export class DisplayClock {
  mode: DisplayMode;
  private shown = Number.NaN;

  constructor(mode: DisplayMode = "strobe") {
    this.mode = mode;
  }

  /** The angle drawn last, or NaN before the first frame. */
  get angle(): number {
    return this.shown;
  }

  /** Draws `theta` next, whatever the mode: after a pause, a scrub or a new model. */
  reset(theta: number): void {
    this.shown = wrapCycle(theta);
  }

  /**
   * The angle to draw this frame.
   * @param heard the crank angle being heard, radians
   * @param omega the crank speed, rad/s (positive in the turning direction)
   * @param dt seconds since the previous frame
   */
  advance(heard: number, omega: number, dt: number): number {
    const theta = wrapCycle(heard);
    if (this.mode === "true-speed" || Number.isNaN(this.shown)) {
      this.shown = theta;
      return theta;
    }
    const limit = Math.min(STROBE_MAX_RATE * Math.max(0, dt), STROBE_MAX_STEP);
    let step: number;
    if (omega > STROBE_MAX_RATE) {
      step = limit;
    } else if (omega < -STROBE_MAX_RATE) {
      step = -limit;
    } else {
      // Slow: close the gap to the heard angle, forward unless it lies just behind.
      let gap = wrapCycle(theta - this.shown);
      if (gap > CYCLE - BACKWARD_WINDOW) gap -= CYCLE;
      step = Math.max(-limit, Math.min(limit, gap));
    }
    this.shown = wrapCycle(this.shown + step);
    return this.shown;
  }
}
